import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DollarSign, Plus, Pencil, Trash2, AlertCircle, CheckCircle, Loader2, X, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { getErrorMessage } from '../services/apiService';
import {
  listServicePricings,
  createServicePricing,
  updateServicePricing,
  deleteServicePricing,
  ServicePricing,
  CreateServicePricingRequest,
  UpdateServicePricingRequest
} from '../services/servicePricingService';
import DefaultPricingTiersPanel from '../components/admin/DefaultPricingTiersPanel';

const PricingManagement: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // State
  const [pricings, setPricings] = useState<ServicePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPricing, setEditingPricing] = useState<ServicePricing | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    serviceType: 'MVR',
    stateCode: '',
    recordType: '',
    costPerLookup: '',
    isActive: true,
    description: ''
  });

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  const SERVICE_TYPES = ['NMVTIS', 'VINDecode', 'NVS', 'MVR'];
  const RECORD_TYPES = ['', 'Title', 'Registration'];

  // Access control
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
    } else if (!user.hasPricingAccess) {
      navigate('/settings');
    }
  }, [user, authLoading, navigate]);

  // Data loading
  const loadPricings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listServicePricings();
      setPricings(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.hasPricingAccess) {
      loadPricings();
    }
  }, [user, loadPricings]);

  // Filter logic
  const filteredPricings = filterType === 'all'
    ? pricings
    : pricings.filter(p => p.serviceType === filterType);

  // Modal handlers
  const openCreateModal = () => {
    setFormData({
      serviceType: 'MVR',
      stateCode: '',
      recordType: '',
      costPerLookup: '',
      isActive: true,
      description: ''
    });
    setEditingPricing(null);
    setModalError('');
    setShowModal(true);
  };

  const openEditModal = (pricing: ServicePricing) => {
    setFormData({
      serviceType: pricing.serviceType,
      stateCode: pricing.stateCode || '',
      recordType: pricing.recordType || '',
      costPerLookup: pricing.costPerLookup.toString(),
      isActive: pricing.isActive,
      description: pricing.description || ''
    });
    setEditingPricing(pricing);
    setModalError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPricing(null);
    setModalError('');
    setFormData({
      serviceType: 'MVR',
      stateCode: '',
      recordType: '',
      costPerLookup: '',
      isActive: true,
      description: ''
    });
  };

  // Save handler
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError('');

    // Validation
    if (!formData.costPerLookup || parseFloat(formData.costPerLookup) < 0) {
      setModalError('Please enter a valid cost per lookup');
      return;
    }

    if (formData.stateCode && formData.stateCode.length !== 2) {
      setModalError('State code must be exactly 2 characters');
      return;
    }

    try {
      setSaving(true);

      if (editingPricing) {
        // Update existing pricing
        const updateData: UpdateServicePricingRequest = {
          costPerLookup: parseFloat(formData.costPerLookup),
          isActive: formData.isActive,
          description: formData.description || undefined
        };
        await updateServicePricing(editingPricing.id, updateData);
        setSuccess('Pricing entry updated successfully');
      } else {
        // Create new pricing
        const createData: CreateServicePricingRequest = {
          serviceType: formData.serviceType,
          stateCode: formData.stateCode || null,
          recordType: formData.recordType || null,
          costPerLookup: parseFloat(formData.costPerLookup),
          isActive: formData.isActive,
          description: formData.description || undefined
        };
        await createServicePricing(createData);
        setSuccess('Pricing entry created successfully');
      }

      await loadPricings();
      closeModal();

      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setModalError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = (pricing: ServicePricing) => {
    setConfirmDialog({
      show: true,
      title: 'Delete Pricing Entry',
      message: `Are you sure you want to delete this pricing entry for ${pricing.serviceType}${pricing.stateCode ? ` (${pricing.stateCode})` : ''}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteServicePricing(pricing.id);
          await loadPricings();
          setSuccess('Pricing entry deleted successfully');
          setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
          setError(getErrorMessage(err));
        } finally {
          setConfirmDialog(null);
        }
      }
    });
  };

  // Format cost as currency
  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(2)}`;
  };

  // Get service type badge color
  const getServiceTypeBadgeColor = (type: string): string => {
    switch (type) {
      case 'NMVTIS':
        return 'bg-blue-100 text-blue-700';
      case 'VINDecode':
        return 'bg-purple-100 text-purple-700';
      case 'NVS':
        return 'bg-orange-100 text-orange-700';
      case 'MVR':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      {/* Main Content */}
      <div className="pt-28 md:pt-32 lg:ml-[220px] flex-1">
        {/* Header */}
        <div className="relative z-20 px-4 md:px-8 py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center space-x-3 mb-2">
              <DollarSign className="w-8 h-8 text-[#c41e3a]" />
              <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a]">
                Pricing Management
              </h1>
            </div>
            <p className="text-[#64748b] text-lg">
              Manage service pricing configuration
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 px-4 md:px-8 py-8">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Success/Error Messages */}
            {error && (
              <div
                className="mb-6 p-4 rounded-xl border flex items-start space-x-3"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  onClick={() => setError('')}
                  className="text-red-600 hover:text-red-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {success && (
              <div
                className="mb-6 p-4 rounded-xl border flex items-start space-x-3"
                style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', borderColor: 'rgba(16, 185, 129, 0.3)' }}
              >
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-emerald-700">{success}</p>
              </div>
            )}

            {/* Filter Bar and Add Button */}
            <div className="glass-card p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilterType('all')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      filterType === 'all'
                        ? 'bg-[#c41e3a] text-white'
                        : 'text-[#64748b] hover:text-[#0f172a] hover:bg-gray-100'
                    }`}
                  >
                    All
                  </button>
                  {SERVICE_TYPES.map(type => (
                    <button
                      key={type}
                      onClick={() => setFilterType(type)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        filterType === type
                          ? 'bg-[#c41e3a] text-white'
                          : 'text-[#64748b] hover:text-[#0f172a] hover:bg-gray-100'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {/* Add Button */}
                <button
                  onClick={openCreateModal}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Pricing</span>
                </button>
              </div>
            </div>

            {/* Data Table */}
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-black/[0.06]">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Service Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        State
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Record Type
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Cost
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-[#94a3b8] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <Loader2 className="w-8 h-8 animate-spin text-[#c41e3a]" />
                          </div>
                        </td>
                      </tr>
                    ) : filteredPricings.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-[#64748b]">
                          No pricing entries found
                        </td>
                      </tr>
                    ) : (
                      filteredPricings.map((pricing) => (
                        <tr
                          key={pricing.id}
                          className="border-t border-black/[0.06] hover:bg-black/[0.02] transition-colors"
                        >
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getServiceTypeBadgeColor(pricing.serviceType)}`}>
                              {pricing.serviceType}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0f172a]">
                            {pricing.stateCode || <span className="text-[#94a3b8] italic">Default</span>}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0f172a]">
                            {pricing.recordType || <span className="text-[#94a3b8] italic">All</span>}
                          </td>
                          <td className="px-6 py-4 text-sm font-semibold text-[#0f172a]">
                            {formatCost(pricing.costPerLookup)}
                          </td>
                          <td className="px-6 py-4">
                            {pricing.isActive ? (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#64748b] max-w-xs truncate">
                            {pricing.description || '-'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => openEditModal(pricing)}
                                className="p-2 text-[#64748b] hover:text-[#c41e3a] hover:bg-[#c41e3a]/10 rounded-lg transition-colors"
                                title="Edit pricing"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(pricing)}
                                className="p-2 text-[#64748b] hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Delete pricing"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <DefaultPricingTiersPanel />
          </div>
        </div>
      </div>

      <Footer />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-[#64748b] hover:text-[#0f172a] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-xl font-semibold text-[#0f172a] mb-6">
              {editingPricing ? 'Edit Pricing' : 'Add Pricing Entry'}
            </h3>

            {modalError && (
              <div
                className="mb-4 p-3 rounded-lg border flex items-start space-x-2"
                style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', borderColor: 'rgba(220, 38, 38, 0.3)' }}
              >
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{modalError}</p>
              </div>
            )}

            <form onSubmit={handleSave} className="space-y-4">
              {/* Service Type */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Service Type <span className="text-red-500">*</span>
                </label>
                {editingPricing ? (
                  <div className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-gray-50 text-[#64748b]">
                    {formData.serviceType}
                  </div>
                ) : (
                  <select
                    value={formData.serviceType}
                    onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                    required
                  >
                    {SERVICE_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* State Code */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  State Code (2 characters, optional)
                </label>
                {editingPricing ? (
                  <div className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-gray-50 text-[#64748b]">
                    {formData.stateCode || 'Default'}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={formData.stateCode}
                    onChange={(e) => setFormData({ ...formData, stateCode: e.target.value.toUpperCase() })}
                    maxLength={2}
                    placeholder="e.g. CA, TX, NY"
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                  />
                )}
              </div>

              {/* Record Type */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Record Type (optional)
                </label>
                {editingPricing ? (
                  <div className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-gray-50 text-[#64748b]">
                    {formData.recordType || 'All'}
                  </div>
                ) : (
                  <select
                    value={formData.recordType}
                    onChange={(e) => setFormData({ ...formData, recordType: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                  >
                    {RECORD_TYPES.map(type => (
                      <option key={type || 'all'} value={type}>
                        {type || 'All'}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Cost Per Lookup */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Cost Per Lookup <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b]">$</span>
                  <input
                    type="number"
                    value={formData.costPerLookup}
                    onChange={(e) => setFormData({ ...formData, costPerLookup: e.target.value })}
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                    required
                  />
                </div>
              </div>

              {/* Is Active */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-[#c41e3a] border-black/[0.08] rounded focus:ring-[#c41e3a]/20 focus:ring-2"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-[#334155]">
                  Active
                </label>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-1.5">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Additional notes or details"
                  className="w-full px-4 py-2.5 rounded-lg border border-black/[0.08] bg-white text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]/20 focus:border-[#c41e3a]"
                />
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2.5 border border-black/[0.08] text-[#64748b] rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-[#c41e3a] text-white rounded-lg hover:bg-[#a01729] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>{editingPricing ? 'Save Changes' : 'Create Pricing'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {confirmDialog?.show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setConfirmDialog(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0f172a] mb-2">{confirmDialog.title}</h3>
            <p className="text-sm text-[#64748b] mb-6">{confirmDialog.message}</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="flex-1 px-4 py-2.5 border border-black/[0.08] text-[#64748b] rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingManagement;
